// src/pages/usermetrics/usermetrics.js
import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Tooltip } from '@mui/material';
import AppDataGrid from '../../components/common/AppDataGrid';
import loading from '../../assets/images/common/loading.gif';
import Container from 'react-bootstrap/Container';
import Card from 'react-bootstrap/Card';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import axios from 'axios';

const UserMetrics = () => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const userData = sessionStorage.getItem('user');
  const userObj = JSON.parse(userData || '{}');

  const [searchQuery, setSearchQuery] = useState('');
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [metrics, setMetrics] = useState([]);

  const [filters, setFilters] = useState({
    metric_id: '',
    accessed: '',
    date: '',
  });

  const handleSearchChange = (event) => setSearchQuery(event.target.value);
  const titleCase = (s = '') =>
    String(s).replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        await fetchMetrics();
        await fetchUserMetrics();
      } catch (error) {
        console.error(error);
        toast.error('Error fetching data');
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    setFilteredRows(rows);
  }, [rows]);

  const fetchMetrics = async () => {
    const resp = await axios.get(
      `${baseUrl}/metricnames/${userObj.school_id}`,
      { params: { action: 'READ' } }
    );

    const payload = resp.data;
    const list = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.data)
        ? payload.data
        : [];

    setMetrics(list);
  };

  const fetchUserMetrics = async () => {
    const resp = await axios.get(
      `${baseUrl}/usermetrics/${userObj.school_id}`,
      { params: { action: 'READ' } }
    );

    const payload = resp.data;
    const list = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.data)
        ? payload.data
        : [];

    setRows(list);
  };

  const columns = [
    {
      field: 'user_name',
      headerName: 'User Name',
      flex: 1,
      renderCell: (p) => (
        <Tooltip title={titleCase(p.value || '')}>
          <span>{titleCase(p.value || '')}</span>
        </Tooltip>
      ),
      sortable: true,
    },
    {
      field: 'metric_name',
      headerName: 'Metric Name',
      flex: 1,
      renderCell: (p) => (
        <Tooltip title={titleCase(p.value || '')}>
          <span>{titleCase(p.value || '')}</span>
        </Tooltip>
      ),
      sortable: true,
    },
    {
      field: 'accessed',
      headerName: 'Accessed',
      flex: 0.6,
      renderCell: (p) => (
        <Tooltip title={p.value ? 'Yes' : 'No'}>
          <span>{p.value ? 'Yes' : 'No'}</span>
        </Tooltip>
      ),
      sortable: true,
    },
    {
      field: 'metric_date',
      headerName: 'Date',
      flex: 0.8,
      renderCell: (p) => {
        const val = p.value || '';
        const short = val.length > 10 ? val.slice(0, 10) : val;
        return (
          <Tooltip title={val}>
            <span>{short}</span>
          </Tooltip>
        );
      },
      sortable: true,
    },
  ];

  const handleFilterChange = (e) => {
    const { id, name, value } = e.target;
    const key = name || id;
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const applyFilters = () => {
    let result = [...rows];

    const q = searchQuery.trim().toLowerCase();
    if (q) {
      result = result.filter((r) => {
        const name = (r.user_name || '').toLowerCase();
        const metric = (r.metric_name || '').toLowerCase();
        return name.includes(q) || metric.includes(q);
      });
    }

   if (filters.metric_name) {
  const selected = String(filters.metric_name).trim().toLowerCase();
  result = result.filter(
    (r) => String(r.metric_name || "").trim().toLowerCase() === selected
  );
}

    if (filters.accessed) {
      const flag = filters.accessed === 'true';
      result = result.filter((r) => !!r.accessed === flag);
    }

    if (filters.date) {
      result = result.filter(
        (r) => (r.metric_date || '').slice(0, 10) === filters.date
      );
    }

    setFilteredRows(result);
  };

  const handleReset = () => {
    setFilters({ metric_id: '', accessed: '', date: '' });
    setSearchQuery('');
    setFilteredRows(rows);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  };

  // 🔍 Auto-search when typing
  useEffect(() => {
    applyFilters();
  }, [searchQuery]);

  // 🔄 Auto-filter when dropdown/radio/date changes
  useEffect(() => {
    applyFilters();
  }, [filters]);

  return (
    <div className="pageBody">
      <ToastContainer position="top-right" autoClose={3000} />

      <Container fluid>
        <Card>
          <Card.Body>
            <form onSubmit={handleSubmit}>
              <Row>
                <Col xs={12}>
                  <h6 className="commonSectionTitle">User Metrics</h6>
                </Col>
              </Row>

              <Row className="align-items-end">
                <Col xs={12} md={6} lg={3} xxl={3}>
                  <div className="commonInput">
                    <Form.Select
                      id="metric_name"
                      value={filters.metric_name || ""}
                      onChange={handleFilterChange}
                    >
                      <option value="" disabled hidden>
                        Select Metric Name
                      </option>

                      {Array.from(
                        new Map(
                          (metrics || [])
                            .filter((m) => (m.metric_name || "").trim())
                            .map((m) => [String(m.metric_name).trim().toLowerCase(), String(m.metric_name).trim()])
                        ).values()
                      )
                        .sort((a, b) => a.localeCompare(b))
                        .map((name) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                    </Form.Select>

                  </div>
                </Col>

                <Col xs={12} md={6} lg={3} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Accessed</Form.Label>
                    </Form.Group>

                    <div className="d-flex gap-3">
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="accessed"
                          id="accessed_yes"
                          value="true"
                          checked={filters.accessed === 'true'}
                          onChange={handleFilterChange}
                        />
                        <label className="form-check-label" htmlFor="accessed_yes">
                          Yes
                        </label>
                      </div>
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="accessed"
                          id="accessed_no"
                          value="false"
                          checked={filters.accessed === 'false'}
                          onChange={handleFilterChange}
                        />
                        <label className="form-check-label" htmlFor="accessed_no">
                          No
                        </label>
                      </div>
                    </div>
                  </div>
                </Col>

                <Col xs={12} md={6} lg={3} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Date</Form.Label>
                      <Form.Control
                        type="date"
                        id="date"
                        value={filters.date}
                        onChange={handleFilterChange}
                      />
                    </Form.Group>
                  </div>
                </Col>

                <Col xs={12} md={6} lg={3} xxl={3} className="mt-3 mt-md-0">
                  <div className="d-flex gap-2 justify-content-start justify-content-md-end">
                    <Button
                      type="button"
                      className="primaryBtn"
                      onClick={applyFilters}
                    >
                      Search
                    </Button>
                    <Button
                      type="button"
                      className="secondaryBtn"
                      variant="outline-secondary"
                      onClick={handleReset}
                    >
                      Reset
                    </Button>
                  </div>
                </Col>
              </Row>
            </form>
          </Card.Body>
        </Card>
      </Container>

      <Row className="mt-3 mb-2 align-items-center">
        <Col md={3}>
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            className="searchInput"
            onChange={handleSearchChange}
          />
        </Col>
      </Row>

      <div className="commonTable">
        <div className="tableBody">
          {isLoading ? (
            <div className="loadingContainer">
              <img src={loading} alt="Loading..." className="loadingGif" />
            </div>
          ) : (
            <AppDataGrid
              rows={filteredRows}
              columns={columns}
              getRowId={(r) => `${r.user_id}-${r.metric_id ?? 0}`}
              showToolbar={false}
              pagination
              pageSizeOptions={[10, 20, 50]}
              density="compact"
              maxHeight={400}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default UserMetrics;
